import styled from 'styled-components';
import { MatchExtra, FormGame } from '../api/client';
import { Flag } from './TeamBadge';
import { H2 } from './ui';

type TeamRef = { name: string; flag: string };

// Previa / herramienta de análisis: "¿quién llega mejor?" — barra de favoritismo (forma + H2H),
// rachas recientes con puntitos de color y el mano a mano. Pensada para enganchar antes del partido.
export function MatchPreview({ data, t1, t2 }: { data: MatchExtra; t1: TeamRef; t2: TeamRef }) {
  const f1 = data.form?.team1 ?? [];
  const f2 = data.form?.team2 ?? [];
  const h2h = data.h2h ?? null;
  if (f1.length === 0 && f2.length === 0 && !h2h) return null;

  const p1 = formPoints(f1);
  const p2 = formPoints(f2);
  const h1 = h2h?.team1Wins ?? 0;
  const h2 = h2h?.team2Wins ?? 0;
  const s1 = p1 + h1 * 2;
  const s2 = p2 + h2 * 2;
  const tot = s1 + s2;
  const pct1 = tot > 0 ? Math.round((s1 / tot) * 100) : 50;
  const pct2 = 100 - pct1;
  const gap = Math.abs(pct1 - 50);
  const verdict =
    gap < 6 ? 'Llegan parejos' : `${(pct1 > pct2 ? t1 : t2).name} llega mejor`;
  const conf = gap < 6 ? 'Mano a mano' : gap < 18 ? 'Ligera ventaja' : 'Favorito claro';
  const prob = win123(s1, s2);

  return (
    <Card>
      <div className="hd">
        <span className="ico">🔮</span> Previa · ¿quién llega mejor?
      </div>

      <Meter>
        <Flag team={t1.flag} size={34} />
        <div className="bar">
          <div className="a" style={{ flex: pct1 }} />
          <div className="b" style={{ flex: pct2 }} />
          <span className="p1">{pct1}%</span>
          <span className="p2">{pct2}%</span>
        </div>
        <Flag team={t2.flag} size={34} />
      </Meter>
      <div className="verdict">
        <strong>{verdict}</strong>
        <span className="conf">{conf}</span>
      </div>

      <H2>Probabilidad</H2>
      <Prob>
        <div className="bar">
          <div className="p1" style={{ flex: Math.max(prob.p1, 1) }}>{prob.p1}%</div>
          <div className="pX" style={{ flex: Math.max(prob.pX, 1) }}>{prob.pX}%</div>
          <div className="p2" style={{ flex: Math.max(prob.p2, 1) }}>{prob.p2}%</div>
        </div>
        <div className="lg">
          <span>
            <i className="c1" /> 1 · gana {t1.name}
          </span>
          <span>
            <i className="cX" /> X · empate
          </span>
          <span>
            <i className="c2" /> 2 · gana {t2.name}
          </span>
        </div>
      </Prob>

      <H2>Forma reciente</H2>
      <FormRow name={t1.name} games={f1} pts={p1} />
      <FormRow name={t2.name} games={f2} pts={p2} />

      {h2h && h2h.total > 0 && (
        <>
          <H2>Mano a mano</H2>
          <H2h>
            <div className="tot">
              <span className="w">{h2h.team1Wins}</span>
              <span className="lbl">{t1.name}</span>
              <span className="d">{h2h.draws} E</span>
              <span className="lbl">{t2.name}</span>
              <span className="l">{h2h.team2Wins}</span>
            </div>
            <div className="hbar">
              <div className="a" style={{ flex: Math.max(h2h.team1Wins, 0.001) }} />
              <div className="d" style={{ flex: Math.max(h2h.draws, 0.001) }} />
              <div className="b" style={{ flex: Math.max(h2h.team2Wins, 0.001) }} />
            </div>
            <div className="games">
              {h2h.games.slice(0, 6).map((g, i) => (
                <div className="g" key={i}>
                  <span className="dt">{g.date}</span>
                  <span className="sc">{g.score}</span>
                  <span className="cp">{g.comp}</span>
                </div>
              ))}
            </div>
          </H2h>
        </>
      )}
      <div className="disc">Análisis orientativo según forma reciente e historial.</div>
    </Card>
  );
}

function FormRow({ name, games, pts }: { name: string; games: FormGame[]; pts: number }) {
  if (games.length === 0) return null;
  return (
    <Form>
      <div className="top">
        <span className="nm">{name}</span>
        <span className="dots">
          {games.map((g, i) => (
            <span key={i} className={`d ${g.result}`} title={`${g.opp} ${g.score}`} />
          ))}
        </span>
        <span className="pts">{pts} pts</span>
      </div>
      <div className="goals">
        ⚽ {avg(games, 'gf').toFixed(1)} GF · {avg(games, 'ga').toFixed(1)} GC{' '}
        <span className="muted">(prom. últimos {games.length})</span>
      </div>
    </Form>
  );
}

function formPoints(games: FormGame[]): number {
  return games.reduce((s, g) => s + (g.result === 'W' ? 3 : g.result === 'D' ? 1 : 0), 0);
}

function avg(games: FormGame[], key: 'gf' | 'ga'): number {
  if (games.length === 0) return 0;
  return games.reduce((s, g) => s + (g[key] ?? 0), 0) / games.length;
}

// Probabilidad orientativa 1 / X / 2 a partir de las fuerzas s1, s2. El empate pesa más
// cuanto más parejas son las fuerzas. Devuelve enteros que suman 100.
function win123(s1: number, s2: number): { p1: number; pX: number; p2: number } {
  const total = s1 + s2;
  if (total === 0) return { p1: 33, pX: 34, p2: 33 };
  const closeness = 1 - Math.abs(s1 - s2) / total;
  const pDraw = Math.min(0.34, Math.max(0.15, 0.18 + 0.16 * closeness));
  const rem = 1 - pDraw;
  const p1 = Math.round(((rem * s1) / total) * 100);
  const p2 = Math.round(((rem * s2) / total) * 100);
  return { p1, p2, pX: 100 - p1 - p2 };
}

const Card = styled.div`
  background: linear-gradient(160deg, ${({ theme }) => theme.colors.accentDark}, ${({ theme }) => theme.colors.surface} 70%);
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px;
  margin: 8px 0 4px;
  color: ${({ theme }) => theme.colors.text};

  .hd {
    font-weight: 800;
    font-size: 1.05rem;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .verdict {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    margin-top: 8px;
    text-align: center;
  }
  .verdict strong { font-size: 1.05rem; }
  .verdict .conf {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.accent};
    font-weight: 700;
  }
  .disc {
    margin-top: 12px;
    font-size: 0.72rem;
    text-align: center;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Meter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  .bar {
    position: relative;
    flex: 1;
    display: flex;
    height: 26px;
    border-radius: 999px;
    overflow: hidden;
    background: ${({ theme }) => theme.colors.surfaceAlt};
    font-size: 0.78rem;
    font-weight: 800;
  }
  .bar .a { background: ${({ theme }) => theme.colors.accent}; }
  .bar .b { background: ${({ theme }) => theme.colors.gold}; }
  .bar .p1 { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #fff; }
  .bar .p2 { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: #fff; }
`;

const Form = styled.div`
  margin-bottom: 10px;
  .top { display: flex; align-items: center; gap: 10px; }
  .nm { flex: 1; font-weight: 700; font-size: 0.95rem; }
  .dots { display: flex; gap: 5px; }
  .dots .d { width: 16px; height: 16px; border-radius: 50%; display: inline-block; }
  .dots .W { background: ${({ theme }) => theme.colors.accent}; }
  .dots .D { background: ${({ theme }) => theme.colors.textMuted}; }
  .dots .L { background: ${({ theme }) => theme.colors.live}; }
  .pts { font-weight: 800; font-variant-numeric: tabular-nums; min-width: 50px; text-align: right; }
  .goals {
    font-size: 0.82rem;
    color: ${({ theme }) => theme.colors.text};
    margin-top: 2px;
  }
  .goals .muted { color: ${({ theme }) => theme.colors.textMuted}; }
`;

const Prob = styled.div`
  .bar {
    display: flex;
    height: 30px;
    border-radius: 8px;
    overflow: hidden;
    font-size: 0.82rem;
    font-weight: 800;
    color: #fff;
  }
  .bar > div {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
  }
  .bar .p1 { background: ${({ theme }) => theme.colors.accent}; }
  .bar .pX { background: ${({ theme }) => theme.colors.textMuted}; }
  .bar .p2 { background: ${({ theme }) => theme.colors.gold}; }
  .lg {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    justify-content: center;
    margin-top: 8px;
    font-size: 0.78rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
  .lg span { display: inline-flex; align-items: center; gap: 5px; }
  .lg i { width: 10px; height: 10px; border-radius: 2px; display: inline-block; }
  .lg .c1 { background: ${({ theme }) => theme.colors.accent}; }
  .lg .cX { background: ${({ theme }) => theme.colors.textMuted}; }
  .lg .c2 { background: ${({ theme }) => theme.colors.gold}; }
`;

const H2h = styled.div`
  .tot {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.9rem;
    margin-bottom: 6px;
  }
  .tot .w { font-weight: 900; font-size: 1.2rem; color: ${({ theme }) => theme.colors.accent}; }
  .tot .l { font-weight: 900; font-size: 1.2rem; color: ${({ theme }) => theme.colors.gold}; }
  .tot .d { font-weight: 700; color: ${({ theme }) => theme.colors.textMuted}; }
  .tot .lbl { font-size: 0.8rem; color: ${({ theme }) => theme.colors.textMuted}; }
  .hbar {
    display: flex;
    height: 8px;
    border-radius: 999px;
    overflow: hidden;
    margin-bottom: 10px;
  }
  .hbar .a { background: ${({ theme }) => theme.colors.accent}; }
  .hbar .d { background: ${({ theme }) => theme.colors.surfaceAlt}; }
  .hbar .b { background: ${({ theme }) => theme.colors.gold}; }
  .games .g {
    display: grid;
    grid-template-columns: auto auto 1fr;
    gap: 10px;
    padding: 5px 2px;
    border-bottom: 1px dashed ${({ theme }) => theme.colors.border};
    font-size: 0.85rem;
  }
  .games .sc { font-weight: 800; font-variant-numeric: tabular-nums; }
  .games .cp { text-align: right; color: ${({ theme }) => theme.colors.textMuted}; }
`;
